// NEW_FILE_JUSTIFIED: Payment settings hook — separate domain from renewal settings
import { useReducer, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';

interface IPaymentSettingsState {
  // LOCAL_TYPE_JUSTIFIED: reducer state only used in this hook
  gracePeriodDays: number;
  smsFallbackDays: number;
  notificationMode: 'automatic' | 'manual';
  emailTemplate: string;
  smsTemplate: string;
  companyEmailTemplate: string;
  isSaving: boolean;
}

type TPaymentSettingsAction = // LOCAL_TYPE_JUSTIFIED: reducer actions only used in this hook
  | { type: 'SET_GRACE_PERIOD_DAYS'; value: number }
  | { type: 'SET_SMS_FALLBACK_DAYS'; value: number }
  | { type: 'SET_NOTIFICATION_MODE'; value: 'automatic' | 'manual' }
  | { type: 'SET_EMAIL_TEMPLATE'; value: string }
  | { type: 'SET_SMS_TEMPLATE'; value: string }
  | { type: 'SET_COMPANY_EMAIL_TEMPLATE'; value: string }
  | { type: 'SET_SAVING'; value: boolean }
  | { type: 'LOAD_DATA'; data: Partial<IPaymentSettingsState> };

const initialState: IPaymentSettingsState = {
  gracePeriodDays: 14,
  smsFallbackDays: 3,
  notificationMode: 'automatic',
  emailTemplate: '',
  smsTemplate: '',
  companyEmailTemplate: '',
  isSaving: false,
};

function paymentSettingsReducer(
  state: IPaymentSettingsState,
  action: TPaymentSettingsAction
): IPaymentSettingsState {
  switch (action.type) {
    case 'SET_GRACE_PERIOD_DAYS':
      return { ...state, gracePeriodDays: action.value };
    case 'SET_SMS_FALLBACK_DAYS':
      return { ...state, smsFallbackDays: action.value };
    case 'SET_NOTIFICATION_MODE':
      return { ...state, notificationMode: action.value };
    case 'SET_EMAIL_TEMPLATE':
      return { ...state, emailTemplate: action.value };
    case 'SET_SMS_TEMPLATE':
      return { ...state, smsTemplate: action.value };
    case 'SET_COMPANY_EMAIL_TEMPLATE':
      return { ...state, companyEmailTemplate: action.value };
    case 'SET_SAVING':
      return { ...state, isSaving: action.value };
    case 'LOAD_DATA':
      return { ...state, ...action.data };
    default:
      return state;
  }
}

export interface IUsePaymentSettingsReturn {
  // LOCAL_TYPE_JUSTIFIED: return type for this hook — consumed by dialog and tab components
  state: IPaymentSettingsState;
  dispatch: React.Dispatch<TPaymentSettingsAction>;
  saveAll: () => Promise<void>;
  isLoading: boolean;
}

export function usePaymentSettings(orgId: Id<'organisations'>): IUsePaymentSettingsReturn {
  const [state, dispatch] = useReducer(paymentSettingsReducer, initialState);

  const gracePeriodDays = useQuery(api.insurance.paymentSettings.getGracePeriodDays, { orgId });
  const smsFallbackDays = useQuery(api.insurance.paymentSettings.getSmsFallbackDays, { orgId });
  const emailTemplate = useQuery(api.insurance.paymentSettings.getEmailTemplate, { orgId });
  const smsTemplate = useQuery(api.insurance.paymentSettings.getSmsTemplate, { orgId });
  const companyEmailTemplate = useQuery(api.insurance.paymentSettings.getCompanyEmailTemplate, {
    orgId,
  });
  const notificationMode = useQuery(api.insurance.paymentSettings.getNotificationMode, { orgId });

  const setGracePeriodDays = useMutation(api.insurance.paymentSettings.setGracePeriodDays);
  const setSmsFallbackDays = useMutation(api.insurance.paymentSettings.setSmsFallbackDays);
  const setEmailTemplate = useMutation(api.insurance.paymentSettings.setEmailTemplate);
  const setSmsTemplate = useMutation(api.insurance.paymentSettings.setSmsTemplate);
  const setCompanyEmailTemplate = useMutation(
    api.insurance.paymentSettings.setCompanyEmailTemplate
  );
  const setNotificationMode = useMutation(api.insurance.paymentSettings.setNotificationMode);

  const isLoading =
    gracePeriodDays === undefined ||
    smsFallbackDays === undefined ||
    emailTemplate === undefined ||
    smsTemplate === undefined ||
    companyEmailTemplate === undefined ||
    notificationMode === undefined;

  useEffect(() => {
    if (isLoading) return;
    dispatch({
      type: 'LOAD_DATA',
      data: {
        gracePeriodDays: gracePeriodDays ?? 14,
        smsFallbackDays: smsFallbackDays ?? 3,
        notificationMode: notificationMode ?? 'automatic',
        emailTemplate: emailTemplate ?? '',
        smsTemplate: smsTemplate ?? '',
        companyEmailTemplate: companyEmailTemplate ?? '',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const saveAll = useCallback(async () => {
    dispatch({ type: 'SET_SAVING', value: true });
    try {
      await Promise.all([
        setGracePeriodDays({ orgId, days: state.gracePeriodDays }),
        setSmsFallbackDays({ orgId, days: state.smsFallbackDays }),
        setNotificationMode({ orgId, mode: state.notificationMode }),
        setEmailTemplate({ orgId, template: state.emailTemplate }),
        setSmsTemplate({ orgId, template: state.smsTemplate }),
        setCompanyEmailTemplate({ orgId, template: state.companyEmailTemplate }),
      ]);
    } finally {
      dispatch({ type: 'SET_SAVING', value: false });
    }
  }, [
    orgId,
    state,
    setGracePeriodDays,
    setSmsFallbackDays,
    setNotificationMode,
    setEmailTemplate,
    setSmsTemplate,
    setCompanyEmailTemplate,
  ]);

  return { state, dispatch, saveAll, isLoading };
}
